import type {
  PatchModel,
  PatchOp,
  PatchOpName,
  PatchOps,
  PatchableDocument,
  PatchableRecord,
} from "@shared/models/patch"
import { batch } from "@supergrain/kernel"
import type { Query, UpdateOperations } from "@supergrain/mill"
import { update } from "@supergrain/mill"

import { clonePrototypeFree } from "@shared/lib/strip-prototypes"

type Rewind = () => void
type MongoUpdate = UpdateOperations<PatchableDocument>
type MongoOperator = "$set" | "$unset" | "$inc" | "$push" | "$pull" | "$pullAll" | "$addToSet"
type PatchOpWithMongoOperator = PatchOpName

const patchOpToMongoOperator = {
  set: "$set",
  unset: "$unset",
  inc: "$inc",
  push: "$push",
  pull: "$pull",
  pullAll: "$pullAll",
  addToSet: "$addToSet",
} satisfies Record<PatchOpWithMongoOperator, MongoOperator>

const patchOpsWithMongoOperators: PatchOpWithMongoOperator[] = [
  "set",
  "unset",
  "inc",
  "push",
  "pull",
  "pullAll",
  "addToSet",
]

export function isPatchableRecord(value: unknown): value is PatchableRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function opEntries(ops: PatchOps | undefined, op: PatchOpName): PatchOp[] {
  return Array.from(new Map(ops?.[op] ?? []))
}

// Values are cloned prototype-free: mill stores exactly the objects we hand
// it (no copying on its side), and stored documents must stay prototype-free
// at every level (see ./strip-prototypes.ts). The clone also decouples the
// stored document from the PatchModel, as cloneDeep did before.
function mongoValue(value: unknown): unknown {
  if (Array.isArray(value) && value[0] === "$each" && Array.isArray(value[1])) {
    return { $each: clonePrototypeFree(value[1]) }
  }

  if (Array.isArray(value) && value[0] === "$_dottedPath_" && typeof value[1] === "string") {
    return clonePrototypeFree({ [value[1]]: value[2] })
  }

  return clonePrototypeFree(value)
}

function setMongoOp(
  updateDocument: MongoUpdate,
  operator: MongoOperator,
  path: string,
  value: unknown
): void {
  updateDocument[operator] ??= {}
  updateDocument[operator][path] = value
}

function addOpsToMongoUpdate(
  updateDocument: MongoUpdate,
  ops: PatchOps | undefined,
  prefix = ""
): void {
  for (const op of patchOpsWithMongoOperators) {
    const operator = patchOpToMongoOperator[op]

    for (const [path, value] of opEntries(ops, op)) {
      setMongoOp(
        updateDocument,
        operator,
        `${prefix}${path}`,
        op === "unset" ? true : mongoValue(value)
      )
    }
  }
}

function embeddedPathPrefix(patch: PatchModel): string | null {
  if (!patch.embeddedDocument?.path) return null
  return `${patch.embeddedDocument.path}.$.`
}

function mongoQueryForPatch(patch: PatchModel): Query<PatchableDocument> {
  if (!patch.embeddedDocument?.path) return {}

  return {
    [patch.embeddedDocument.path]: {
      $elemMatch: clonePrototypeFree(patch.embeddedDocument.elemMatch),
    },
  }
}

export function patchToMongoUpdate(patch: PatchModel): MongoUpdate {
  const updateDocument: MongoUpdate = {}

  addOpsToMongoUpdate(updateDocument, patch.document.ops)

  const prefix = embeddedPathPrefix(patch)
  if (prefix) {
    addOpsToMongoUpdate(updateDocument, patch.embeddedDocument?.ops, prefix)
  }

  setMongoOp(updateDocument, "$inc", "internalVersion", 1)

  return updateDocument
}

export function patchSupergrainDocument(doc: PatchableDocument, patch: PatchModel): Rewind {
  // We need allowNullIntermediates because Ecto converts absent top-level fields
  // in Mongo documents to nil, which becomes null on the frontend.
  // Without this option, patches that do work in MongoDB (where fields are absent)
  // would fail in Supergrain (where fields are null).
  return batch(() => {
    const { undo } = update(doc, mongoQueryForPatch(patch), patchToMongoUpdate(patch), {
      allowNullIntermediates: true,
    })
    return () => batch(() => update(doc, {}, undo, { allowNullIntermediates: true }))
  })
}
